import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Lock, Save } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';

export default function Settings() {
  const { t } = useI18n();
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [picture, setPicture] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    setName(user.name || '');
    setPicture(user.picture || '');
  }, [user, loading, navigate]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {};
      if (name && name !== user.name) payload.name = name;
      if (picture !== user.picture) payload.picture = picture;
      const { data } = await api.patch('/auth/me', payload);
      setUser(data);
      toast.success('Profilo aggiornato');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.common.error);
    } finally {
      setSavingProfile(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error('Min 8 caratteri'); return; }
    setChangingPw(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPassword || null, new_password: newPassword });
      toast.success('Password aggiornata');
      setCurrentPassword(''); setNewPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.common.error);
    } finally {
      setChangingPw(false);
    }
  };

  if (loading || !user) return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">{t.common.loading}</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight">Impostazioni</h1>

      <section className="mt-12 p-8 rounded-3xl border border-border bg-card">
        <div className="flex items-center gap-3 mb-6">
          <User size={18} className="text-brand-gold-dark" />
          <h2 className="font-display text-xl font-medium">Profilo</h2>
        </div>
        <form onSubmit={saveProfile} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Email</label>
            <input type="email" value={user.email} disabled data-testid="settings-email-display"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-secondary border border-border text-muted-foreground cursor-not-allowed" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Nome</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Immagine profilo (URL, opzionale)</label>
            <input type="url" value={picture} onChange={(e) => setPicture(e.target.value)} data-testid="settings-picture-input"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
          </div>
          <button type="submit" disabled={savingProfile} data-testid="settings-save-profile-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 transition-opacity">
            <Save size={16} /> {savingProfile ? t.common.loading : 'Salva profilo'}
          </button>
        </form>
      </section>

      <section className="mt-8 p-8 rounded-3xl border border-border bg-card">
        <div className="flex items-center gap-3 mb-6">
          <Lock size={18} className="text-brand-gold-dark" />
          <h2 className="font-display text-xl font-medium">Password</h2>
        </div>
        <form onSubmit={changePw} className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Password attuale</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="settings-current-pw-input"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
            <p className="mt-1.5 text-xs text-muted-foreground">Lascia vuoto se il tuo account è Google e non hai mai impostato una password.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Nuova password</label>
            <input type="password" required minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="settings-new-pw-input"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
          </div>
          <button type="submit" disabled={changingPw} data-testid="settings-change-pw-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 transition-opacity">
            <Save size={16} /> {changingPw ? t.common.loading : 'Aggiorna password'}
          </button>
        </form>
      </section>

      <section className="mt-8 p-8 rounded-3xl border border-border bg-card">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-2">Account</div>
        <div className="text-sm">
          <div><span className="text-muted-foreground">Provider auth:</span> {user.auth_provider}</div>
          <div><span className="text-muted-foreground">Ruolo:</span> <span className={user.role === 'admin' ? 'text-brand-gold-dark font-medium' : ''}>{user.role}</span></div>
        </div>
      </section>
    </div>
  );
}
